/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 *  Copyright © 2020 ООО АДАПТ info@acfs.spb.ru
 *
 *  You may use, distribute and modify this code under the
 *  terms of the ООО АДАПТ license
 */

import React, { FC, useState } from 'react'

import { AOSRType, SecondaryActType } from '@builddocs/filestore-api'
import { useSearchParams } from 'react-router-dom'

import { InteractiveActView } from './InteractiveActView'

type ownProps = {
  act: AOSRType | SecondaryActType
  visible: boolean
  rights: {
    canEdit: boolean
    canInspect: boolean
    canSendActToSigning: boolean
    canSignAct: boolean
  }
  canShowSigningActions: boolean
  versionRemarkExon: number | null
  toReopenInspection: boolean
  onDeviate: (
    body: Record<string, any>,
    additionalInfo: Record<string, any>,
    onSuccess: () => any,
  ) => void
  onReload: () => void
  onClose?: () => void
}

type InteractiveViewerContextType = {
  setViewer: ({
    act,
    visible,
    rights,
    canShowSigningActions,
    versionRemarkExon,
    toReopenInspection,
    onDeviate,
    onReload,
    onClose,
  }: ownProps) => void
}

export const InteractiveViewerContext =
  React.createContext<InteractiveViewerContextType>({
    setViewer: () => null,
  })

export const InteractiveViewerProvider: FC = ({ children }) => {
  const [act, setAct] = useState<AOSRType | SecondaryActType>()
  const [visible, setVisible] = useState<boolean>(false)
  const [rights, setRights] = useState({
    canEdit: false,
    canInspect: false,
    canSendActToSigning: false,
    canSignAct: false,
  })
  const [canShowSigningActions, setCanShowSigningActions] =
    useState<boolean>(false)
  const [toReopenInspection, setToReopenInspectionCanEdit] =
    useState<boolean>(false)
  const [versionRemarkExon, setVersionRemarkExon] = useState<number | null>(
    null,
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const [onReload, setOnReload] = useState<() => void>()
  const [onClose, setOnClose] = useState<() => void>()
  const [onDeviate, setOnDeviate] =
    useState<
      (
        body: Record<string, any>,
        additionalInfo: Record<string, any>,
        onSuccess: () => any,
      ) => void
    >()

  const setViewer = ({
    act,
    visible,
    rights,
    versionRemarkExon,
    onDeviate,
    onReload,
    onClose,
    toReopenInspection,
    canShowSigningActions,
  }: ownProps) => {
    setAct(act)
    setVisible(visible)
    setOnDeviate(() => onDeviate)
    setRights(rights)
    setVersionRemarkExon(() => versionRemarkExon)
    setOnReload(() => onReload)
    setOnClose(() => onClose)
    setToReopenInspectionCanEdit(() => toReopenInspection)
    setCanShowSigningActions(() => canShowSigningActions)
  }
  const onCloseHandle = () => {
    setVisible(false)
    searchParams.delete('open')

    setSearchParams(searchParams)

    onClose && onClose()
  }

  return (
    <InteractiveViewerContext.Provider
      value={{
        setViewer,
      }}
    >
      {act && (
        <InteractiveActView
          act={act}
          canShowSigningActions={canShowSigningActions}
          rights={rights}
          toReopenInspection={toReopenInspection}
          versionRemarkExon={versionRemarkExon}
          visible={visible}
          onClose={onCloseHandle}
          onDeviate={onDeviate}
          onReload={onReload}
        />
      )}
      {children}
    </InteractiveViewerContext.Provider>
  )
}

export default InteractiveViewerProvider
